// NativeBootstrap — wires Capacitor-only side effects + idle-time perf warmup.
//
// Mounted once inside AuthProvider so we have access to the API client.
// On web (Capacitor.getPlatform() !== "ios") the native bridges no-op, but the
// idle-time chunk prefetch still runs — so every bottom-nav destination is
// already in the browser cache by the time the user taps anything.
import { useEffect, useRef } from "react";
import { useAuth } from "@/auth/AuthContext";
import api from "@/api";
import { warmAllNavChunks } from "@/lib/prefetchChunks";

export default function NativeBootstrap() {
    const { user } = useAuth();
    const boundRef = useRef(false);

    useEffect(() => {
        // Only bind once per app lifetime, and only after we have a logged-in
        // user (so the device token POST is authenticated and tagged with
        // the right user_id).
        if (boundRef.current || !user) return;
        boundRef.current = true;

        // Fire idle-time chunk warmup IMMEDIATELY — runs on web + iOS, low
        // priority via requestIdleCallback, staggered 250ms apart so a 5G
        // mobile connection isn't saturated. By the time the user taps a
        // bottom-nav tab, that page's JS is already in the browser cache.
        warmAllNavChunks();

        (async () => {
            try {
                const { bindPushHandlers, syncHealthLast24h } = await import("@/native/bridge");
                await bindPushHandlers(api);

                // Helper that runs one sync + an auto-backfill so any holes
                // (e.g. samples that arrived on the device while WYNGX was
                // closed) get folded into metric_snapshots immediately.
                const runFullSync = () => syncHealthLast24h(api)
                    .then((res) => {
                        // Tell the UI a sync just completed (used by the
                        // dashboard's freshness indicator).
                        window.dispatchEvent(new CustomEvent("wyng:health-synced", {
                            detail: { at: Date.now(), synced: res?.synced || 0 },
                        }));
                        if (res?.synced > 0) {
                            // Refresh aggregates so Twin Battle / Shadow /
                            // Dashboard pick up the new numbers right away.
                            api.post("/health/snapshots/backfill?days=7").catch(() => {});
                        }
                    })
                    .catch((e) => console.debug("[native] sync skipped:", e?.message));

                // 1) First sync on app boot (after login).
                runFullSync();

                // 2) Recurring sync every 5 min while app is in the foreground.
                //    iOS itself throttles WKWebView timers to ~1/min while
                //    backgrounded, so this naturally stops costing battery
                //    when the user puts WYNGX away.
                const FIVE_MIN = 5 * 60 * 1000;
                const interval = setInterval(runFullSync, FIVE_MIN);
                window.addEventListener("beforeunload", () => clearInterval(interval));

                // 3) Foreground resume sync — fires the moment iOS hands
                //    control back to the WebView after the user re-opened
                //    the app from the home screen / app switcher.
                try {
                    const { App } = await import("@capacitor/app");
                    App.addListener("appStateChange", ({ isActive }) => {
                        if (isActive) runFullSync();
                    });
                } catch {
                    /* @capacitor/app not present on web */
                }

                // 4) Smart push (opt-in): if the user enabled it in
                //    Settings, register the HKObserverQuery so iOS can
                //    wake WYNGX *event-driven* when new health samples
                //    are saved. Way cheaper than 15-min BGTask polling.
                if (localStorage.getItem("wyng:bg_sync_enabled") === "1") {
                    try {
                        const { enableBackgroundSync } = await import("@/native/bridge");
                        await enableBackgroundSync(api);
                    } catch (e) {
                        console.debug("[native] observer skipped:", e?.message);
                    }
                }
            } catch (e) {
                // No-op on web or if native plugin not available.
                console.debug("[native] bootstrap skipped:", e?.message);
            }
        })();
    }, [user]);

    return null;
}
